import React, { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";

// Staff-facing, READ-ONLY view of the caller's own career events (promotion /
// grade / title / transfer requests). Self-hides (renders null) when the caller
// has none — so it adds nothing for most staff. Unlike MyPIPCard, /mine here
// returns a BARE ARRAY of all events across statuses.

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

const STATUS_STYLE = {
  pending_hr: "text-amber-400 bg-amber-500/10 border-amber-500/25",
  approved:   "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/25",
  rejected:   "text-red-400 bg-red-500/10 border-red-500/20",
};
const STATUS_FALLBACK = "text-white/30 bg-white/5 border-white/10";
const STATUS_LABEL = { pending_hr: "Pending HR", approved: "Approved", rejected: "Rejected" };
const EVENT_LABEL = { promotion: "Promotion", grade_change: "Grade Change", title_change: "Title Change", transfer: "Transfer" };

const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");
const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const statusBadge = (s) => (
  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[s] || STATUS_FALLBACK}`}>{STATUS_LABEL[s] || s || "—"}</span>
);

export default function MyCareerCard() {
  const [events, setEvents] = useState(null); // null = loading, [] = none
  const token = localStorage.getItem("token");

  useEffect(() => {
    let active = true;
    fetch(`${API_BASE}/api/performance/career-events/mine`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (active) setEvents(Array.isArray(d) ? d : []); })
      .catch(() => { if (active) setEvents([]); });
    return () => { active = false; };
  }, [token]);

  // Loading or none → render nothing (quiet empty state, never an error box).
  if (!events || events.length === 0) return null;

  return (
    <div className={`${card} p-5 space-y-3`}>
      <div className="flex items-center gap-2">
        <TrendingUp size={16} className="text-[#00ff90] shrink-0" />
        <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Career Events</p>
      </div>

      <div className="space-y-2">
        {events.map((e) => (
          <div key={e.id} className="rounded-lg border border-white/5 bg-white/3 p-3 space-y-1.5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-sm text-white/80 font-medium">{EVENT_LABEL[e.event_type] || e.event_type}</span>
              {statusBadge(e.status)}
            </div>
            <div className="text-xs text-white/50 space-y-0.5">
              <p>Title: <span className="text-white/70">{val(e.current_title)} → {val(e.proposed_title)}</span></p>
              <p>Grade: <span className="text-white/70">{val(e.current_grade)} → {val(e.proposed_grade)}</span></p>
              <p className="text-white/30">Effective: {fmtDate(e.effective_date)}</p>
            </div>
            {/* Surface the HR decision note on decided events so staff see the outcome. */}
            {(e.status === "approved" || e.status === "rejected") && e.hr_notes && (
              <p className="text-xs text-white/50">
                <span className="text-white/30">{e.status === "rejected" ? "Reason: " : "HR note: "}</span>{e.hr_notes}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
